/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file accountSlice.js
 * @description Redux slice for managing user account data, including loading, creating, and updating account information.
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the account slice
 * @param {Object|null} accountData - Initial account data if available
 * @returns {Object} The initial state object
 */
export const getInitialState = (accountData = null) => ({
  accountData,
  accountFieldErrors: null,
  error: null,
  loading: false,
  tokenValidationError: null,
  validToken: false,
  validatingToken: false,
});

/**
 * @function loadAccountData
 * @description Async thunk to load account data from the API
 * @param {Object} params - Request parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} params.authToken - Authentication token
 * @param {string} params.language - User language preference
 * @param {string} params.lineId - Line identifier
 * @param {string} params.originId - Origin identifier
 * @returns {Promise<Object>} Account data response
 */
export const loadAccountData = createAsyncThunk(
  "account/loadAccountData",
  async ({ accountId, authToken, language, lineId, originId }, thunkAPI) => {
    try {
      const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language }).get(
        `/player/acc/${accountId}?originId=${originId}&lineId=${lineId}`,
      );

      return { accountData: result.data };
    } catch (err) {
      const customError = {
        message: err.response?.headers["x-information"] || "Unable to load account information", // serializable (err.response.data)
        name: "Load Account Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @function createAccount
 * @description Async thunk to create a new user account
 * @param {Object} data - Account creation data
 * @param {Object} data.user - User information
 * @returns {Promise<Object>} The created account data
 */
export const createAccount = createAsyncThunk("account/createAccount", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const { user } = data;
    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.post(`/player/acc?originId=${originId}&lineId=${lineId}`, { ...user, lineId, originId });

    return {
      accountData: result.data,
    };
  } catch (err) {
    throw thunkAPI.rejectWithValue(err?.response?.data || err);
  }
});

/**
 * @function updateAccount
 * @description Async thunk to update an existing user account
 * @param {Object} data - Account update data
 * @param {string} data.accountId - Account identifier
 * @param {Object} data.user - Updated user information
 * @returns {Promise<Object>} The updated account data
 */
export const updateAccount = createAsyncThunk("account/updateAccount", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());
    const { accountId, user } = data;

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.put(`/player/acc/${accountId}?originId=${originId}&lineId=${lineId}`, {
      ...user,
      lineId,
      originId,
    });

    return {
      accountData: result.data,
    };
  } catch (err) {
    throw thunkAPI.rejectWithValue(err?.response?.data || err);
  }
});
/**
 * @function updatePassword
 * @description Async thunk to update a user's password
 * @param {Object} params - Password update parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} params.oldPassword - Current password
 * @param {string} params.password - New password
 * @returns {Promise<Object>} Password update result
 */
export const updatePassword = createAsyncThunk(
  "account/updatePassword",
  async ({ accountId, oldPassword, password }, thunkAPI) => {
    try {
      const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

      const result = await createAxiosInstance(thunkAPI.dispatch, { authToken, language }).put(
        `/player/acc/${accountId}/pwd?originId=${originId}&lineId=${lineId}`,
        {
          oldPassword,
          password,
        },
      );

      return { result };
    } catch (err) {
      const fieldErrors = {};
      err.response?.data?.errors?.forEach(({ code, message }) => {
        fieldErrors[code] = message;
      });

      const customError = {
        message: err.response?.headers["x-information"] || "Unable to update account", // serializable (err.response.data)
        name: "Error Updating Account",
        status: err.response?.statusText,
        errors: fieldErrors,
      };
      return thunkAPI.rejectWithValue(customError);
    }
  },
);

/**
 * @function requestPasswordReset
 * @description Async thunk to request a password reset
 * @param {Object} data - Password reset request data
 * @param {string} data.email - User email
 * @param {string} data.mobile - User mobile number
 * @returns {Promise<Object>} Empty object on success
 */
export const requestPasswordReset = createAsyncThunk("account/requestPasswordReset", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    await axios.post(`/player/acc/passwordresetrequest?originId=${originId}&lineId=${lineId}`, {
      email: data.email,
      mobile: data.mobile,
    });

    return {};
  } catch (err) {
    throw thunkAPI.rejectWithValue(err?.response?.data || err);
  }
});

/**
 * @function passwordResetValidate
 * @description Async thunk to validate a password reset token
 * @param {Object} data - Token validation data
 * @param {string} data.token - Password reset token
 * @returns {Promise<Object>} Token validation result
 */
export const passwordResetValidate = createAsyncThunk("account/passwordResetValidate", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    const result = await axios.post(`/player/acc/passwordresetvalidate?originId=${originId}&lineId=${lineId}`, {
      token: data.token,
    });

    return { ...result.data };
  } catch (err) {
    throw thunkAPI.rejectWithValue(err?.response?.data || err);
  }
});

/**
 * @function passwordResetConfirm
 * @description Async thunk to confirm a password reset
 * @param {Object} data - Password reset confirmation data
 * @param {string} data.password - New password
 * @param {string} data.token - Password reset token
 * @returns {Promise<Object>} Empty object on success
 */
export const passwordResetConfirm = createAsyncThunk("account/passwordResetConfirm", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });
    await axios.post(`/player/acc/passwordresetconfirm?originId=${originId}&lineId=${lineId}`, {
      password: data.password,
      token: data.token,
    });

    return {};
  } catch (err) {
    throw thunkAPI.rejectWithValue(err?.response?.data || err);
  }
});

/**
 * @constant accountSlice
 * @description Redux slice for account management
 */
const accountSlice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(loadAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAccountData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loadAccountData.fulfilled, (state, action) => {
        state.accountData = action.payload.accountData;
        state.error = null;
        state.loading = false;
      })
      .addCase(createAccount.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.accountFieldErrors = action.payload;
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.accountData = action.payload.accountData;
        state.accountFieldErrors = null;
        state.error = null;
        state.loading = false;
      })
      .addCase(updateAccount.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
        // TODO: populate the error fields
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.accountData = action.payload.accountData;
        state.error = null;
        state.loading = false;
      })
      .addCase(passwordResetValidate.pending, (state) => {
        state.tokenValidationError = null;
        state.validatingToken = true;
        state.validToken = false;
      })
      .addCase(passwordResetValidate.rejected, (state, action) => {
        state.tokenValidationError = action.error.message;
        state.validatingToken = false;
      })
      .addCase(passwordResetValidate.fulfilled, (state, action) => {
        state.validToken = action.payload.valid;
        state.tokenValidationError = action.error?.message || action.payload.error;
        state.validatingToken = false;
      }),
  initialState: getInitialState(),
  name: "account",
  // reducers actions
  reducers: {
    //
  },
});
const { reducer } = accountSlice;
export default reducer;
